import { getCustomHeaders } from '@/components/marketing-landing-page/utils';
import { AUTH_CONFIG } from '@/config/auth.config';
import { COUNTRY_CODE, REFRESH_TOKEN } from '@/constants/common';
import { useGlobal } from '@/contexts/GlobalContext';
import { useUser } from '@/contexts/UserContext';
import { Client } from '@/lib/api';
import { moengageLogout } from '@/lib/events';
import { LoginEventMetadata } from '@/types/login-event-metadata';
import { clearLocalStorage, getLocalStorageValue } from '@/utils/common';
import { CookieKeys, CookieService } from '@/utils/cookie-service';
import { getAuthSdkConfigByOrg, getOrgConfigByHost } from '@/utils/utils';
import { AuthService } from '@pw-tech/web-sdk';
import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import uuid from 'react-uuid';

const TOO_MANY_OTP_ERROR_MESSAGE = 'Too many OTP requests, Please try after sometime.';

export const useAuth = () => {
  const { host } = useGlobal();
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  const [otpSent, setOTPSent] = useState(false);
  const [error, setError] = useState('');
  const [randomId, setRandomId] = useState('');
  const { updateUser, user } = useUser();

  const handleMultipleOtpError = (error: any) => {
    if (error?.status === 400 && error?.message?.includes('Too many OTP requests')) {
      toast.error(TOO_MANY_OTP_ERROR_MESSAGE);
    }
  };

  const resetOnLogout = () => {
    if (!window || (window && typeof window === 'undefined')) return;

    if (host) {
      const orgDetails = getOrgConfigByHost(host);
      const authSdkConfig = getAuthSdkConfigByOrg(orgDetails);
      // initialize the SDK again or update the removed config
      if (window?.PWWebSDK && !window?.PWWebSDK?.config) {
        // update the auth config
        window.PWWebSDK.updateConfig(authSdkConfig);
      } else if (!window?.PWWebSDK) {
        // initialize again
        AuthService.getInstance(authSdkConfig);
      }

      // reset the token context with randomId if not present
      if (!window?.PWWebSDK?.randomId || !getLocalStorageValue('randomId')) {
        if (!getLocalStorageValue('AUTH_CONFIG')) {
          window.PWWebSDK.storage.set('AUTH_CONFIG', authSdkConfig);
        }
        window.PWWebSDK.setTokenContext = {
          randomId: uuid(),
        };
      }
    }
  };

  const handleLogout = async () => {
    const countryGroup = user?.countryGroup;
    const locationValue = localStorage.getItem('COUNTRY_LOCATION');
    if (typeof window !== 'undefined' && window.PWWebSDK) {
      await window.PWWebSDK.logout();
    }
    clearLocalStorage();
    moengageLogout();
    resetOnLogout();
    if (locationValue) {
      localStorage.setItem('COUNTRY_LOCATION', locationValue);
    }
    if (typeof window !== 'undefined' && window.PWWebSDK) {
      window.PWWebSDK.updateConfig(AUTH_CONFIG as any);
    }
    if (!pathname.includes('/marketing')) {
      if (countryGroup) {
        window.location.replace(`${window.location.origin}/${countryGroup.toLowerCase()}`);
      } else window.location.replace(window.location.origin);
    }
    updateUser(null);
  };

  const reset = () => {
    setOTPSent(false);
    setError('');
  };

  const getMe = async () => {
    // Check if user has a valid token before making API call
    const token =
      CookieService.getValue(CookieKeys.TOKEN_CONTEXT)?.token ||
      localStorage.getItem('TOKEN') ||
      localStorage.getItem('token');

    if (!token) {
      console.log('No token found, skipping getMe API call');
      return null;
    }

    try {
      const usersResponse = await Client.get('v1/users/my-profile');
      if (usersResponse?.data) {
        localStorage.setItem('user', JSON.stringify(usersResponse?.data));
        return usersResponse.data;
      }
    } catch (error) {
      console.error('Error in getMe API call:', error);
      // If API call fails (e.g., 401 Unauthorized), return null
      return null;
    }

    return null;
  };

  const checkUserExistsForOtpLess = async (phone: string) => {
    const customHeaders = getCustomHeaders({ org: 'CJR' });
    try {
      const data = {
        username: phone,
        organizationId: process.env.NEXT_PUBLIC_CURIOUSJR_ORG_ID,
      };

      return await Client.post('v3/users/public/trigger-flow', data, { customHeaders });
    } catch (error) {
      console.log('Error checking user existence:', error);
      return null;
    }
  };

  const exchangeToken = async () => {
    const usersResponse = await Client.get('v3/oauth/exchange-token', {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/plain, /',
        randomId: randomId,
        client: 'hasura',
        client_type: 'WEB',
        'client-type': 'WEB',
      },
    });
    if (usersResponse?.data) {
      localStorage.setItem('token', usersResponse?.data.access_token);
      localStorage.setItem('TOKEN', usersResponse?.data.access_token);
      localStorage.setItem(REFRESH_TOKEN, usersResponse?.data.refresh_token);
      localStorage.setItem('expires_in', usersResponse?.data.expires_in);
    }
  };

  const setUserDetails = async (response: any) => {
    let username = response?.data?.user?.firstName;

    if (response?.data?.user?.lastName) {
      username += ' ' + response?.data?.user?.lastName;
    }

    CookieService.setValue(CookieKeys.TOKEN_CONTEXT, {
      token: response?.data?.access_token,
      username,
      [REFRESH_TOKEN]: response?.data.refresh_token,
      expires_in: response?.data.expires_in,
    });
    localStorage.setItem('token', response?.data?.access_token);
    localStorage.setItem('TOKEN', response?.data?.access_token);
    localStorage.setItem('username', username);
    localStorage.setItem(REFRESH_TOKEN, response?.data.refresh_token);
    localStorage.setItem('expires_in', response?.data.expires_in);
  };

  const handleLogin = async (
    otp: string,
    username: string,
    utm_source: string,
    utm_medium: string,
    utm_campaign: string,
    device: string,
    location?: string,
    pageType?: string,
    mlpName?: string,
    mlpType?: string,
    loginEventMetadata?: LoginEventMetadata
  ) => {
    const data = {
      otp: otp,
      client_id: 'system-admin',
      organizationId: process.env.NEXT_PUBLIC_PP_ORG_ID,
      username,
    };
    try {
      setError('');
      setLoading(true);
      await window.PWWebSDK.verifyOTP(
        username,
        otp,
        1000 * 60 * 60 * 24 * 7,
        utm_source,
        utm_medium,
        utm_campaign,
        device,
        '',
        pageType,
        mlpName,
        mlpType,
        loginEventMetadata
      );
      setOTPSent(true);
      setLoading(false);
      return true;
    } catch (error: any) {
      console.log(error);
      console.log('error');
      setLoading(false);
      setError(error?.message);
      return false;
    }
  };

  const handleUpdateUser = async (data: any) => {
    try {
      setError('');
      setLoading(true);
      const resp = await Client.put(`v1/users`, data, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/plain, /',
          randomId: randomId,
          client: 'hasura',
          client_type: 'WEB',
        },
      });
      setLoading(false);
      return resp;
    } catch (error) {
      setLoading(false);
      return error;
    }
  };

  const handleRegister = async ({
    mobile,
    firstName,
    lastName,
    email,
    countryCode,
    smsType,
    successCallback,
    loginEventMetadata,
  }: {
    mobile: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    countryCode?: string;
    smsType: number;
    successCallback?: () => void;
    loginEventMetadata?: LoginEventMetadata;
  }) => {
    const data = {
      mobile,
      firstName,
      lastName: lastName || '',
      email,
      countryCode: countryCode || '+91',
    };

    try {
      setError('');
      setLoading(true);
      await window.PWWebSDK.registerUser({
        phoneNumber: mobile,
        countryCode: countryCode || '+91',
        firstName: firstName || '',
        lastName: lastName || '',
        smsType,
        loginEventMetadata,
      } as any);
      setOTPSent(true);
      setLoading(false);
      successCallback?.();
    } catch (error: any) {
      if (error?.message?.includes('Too many OTP requests')) {
        toast.error(TOO_MANY_OTP_ERROR_MESSAGE);
      }
      //   eventTracker.otpFailed({
      //     phoneNumber: data.mobile,
      //     error: JSON.stringify(error),
      //     type: 'register',
      //   })
      setLoading(false);
    }
  };

  const handleGenerateOTP = async (phone: string, smstype: number, countryCode?: string) => {
    const data = {
      username: phone,
      countryCode: countryCode || '+91',
      organizationId: process.env.NEXT_PUBLIC_PP_ORG_ID,
    };
    try {
      setError('');
      setLoading(true);
      const response = await window.PWWebSDK.getOTP(phone, data?.countryCode, smstype);

      // API throwed error
      if (response?.success === false) {
        if (response?.message?.includes('Too many OTP requests')) {
          toast.error(TOO_MANY_OTP_ERROR_MESSAGE);
        }
        // eventTracker.otpFailed({
        //   phoneNumber: data.username,
        //   error: JSON.stringify(response),
        //   type: 'get-otp',
        // })
        setLoading(false);
        return false;
      }
      setLoading(false);
      setOTPSent(true);
      return !response?.newUser;
    } catch (error: any) {
      //   eventTracker.otpFailed({
      //     phoneNumber: data.username,
      //     error: JSON.stringify(error),
      //     type: 'get-otp',
      //   })
      setLoading(false);
      return false;
    }
  };

  const handleIsUserValidate = async ({
    countryCode,
    mobile,
    smsType,
  }: {
    countryCode: string;
    mobile: string;
    smsType: number;
  }) => {
    const data = {
      countryCode,
      mobile,
    };
    const ORGANISATION_ID = process.env.NEXT_PUBLIC_PP_ORG_ID;
    try {
      setError('');
      setLoading(true);
      await Client.post(`v3/users/validate-user/${ORGANISATION_ID}?smsType=${smsType}`, data, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/plain, /',
          randomId: randomId,
          client_type: 'WEB',
        },
      });
      setOTPSent(true);
      setLoading(false);
      return true;
    } catch (error) {
      setLoading(false);
      console.log(error);
      return false;
    }
  };

  const resendOTP = async (phone: string, smstype: number, countryCode?: string) => {
    const data = {
      mobile: phone,
      countryCode: countryCode || localStorage.getItem(COUNTRY_CODE) || '+91',
      organizationId: process.env.NEXT_PUBLIC_PP_ORG_ID,
    };
    try {
      setError('');
      setLoading(true);
      const res = await Client.post(`v1/users/resend-otp?smsType=${smstype}`, data, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/plain, /',
          randomId: randomId,
          client_type: 'WEB',
        },
      });
      if (res.data) {
        setOTPSent(true);
        setLoading(false);
        return res.data.success;
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
      return false;
    }
  };

  const handleGoogleLogin = async (data: any) => {
    try {
      setError('');
      const response = await Client.post('v3/oauth/social-connect', data, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/plain, /',
          randomId: randomId,
          client: 'hasura',
          client_type: 'WEB',
        },
      });

      if (response?.data?.user) {
        setUserDetails(response);
        return true;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const fetchAuthToken = async (data: any) => {
    setLoading(true);
    const res = await Client.post(`v1/oauth/token`, {
      client_id: process.env.NEXT_PUBLIC_AUTH_TOKEN_CLIENT_ID,
      client_secret: process.env.NEXT_PUBLIC_AUTH_TOKEN_CLIENT_SECRET,
      grant_type: 'password',
      organizationId: process.env.NEXT_PUBLIC_ORG_ID,
      ...data,
    });
    setLoading(false);
    if (res?.data?.access_token) {
      const { access_token: token, user } = res?.data;
      localStorage.setItem('token', token);
      localStorage.setItem('TOKEN', token);
      localStorage.setItem('user', JSON.stringify(user));
      //   handleUserUpdated()
    }
  };

  const resendOTPBySmsType = async (smsType: any) => {
    const data = {
      mobile: localStorage.getItem('username'),
      organizationId: process.env.NEXT_PUBLIC_PP_ORG_ID,
    };

    try {
      setError('');
      setLoading(true);

      await Client.post(`/v1/users/resend-otp?smsType=${smsType}`, data, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/plain, /',
          randomId: randomId,
          client: 'hasura',
          client_type: 'WEB',
        },
      });
      setLoading(false);
      setOTPSent(true);
      return true;
    } catch (error) {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    exchangeToken,
    handleLogout,
    getMe,
    resendOTP,
    handleLogin,
    handleRegister,
    handleUpdateUser,
    otpSent,
    reset,
    handleGenerateOTP,
    handleGoogleLogin,
    fetchAuthToken,
    resendOTPBySmsType,
    handleIsUserValidate,
    checkUserExistsForOtpLess,
    resetOnLogout,
  };
};
